/**
 * Every call this app makes, and the types that come back.
 *
 * The portal's response shapes are inconsistent by history — `{ status, data }`
 * on new endpoints, `{ result }` on batches, bare arrays on user lookups, and a
 * bare `{ accessToken }` on login. All of that is normalised here so no screen
 * has to know. See docs/api-contract.md.
 */

import { ApiError, request, unwrap } from "./client";

/** 12 chars: `ZB` + 10 from an alphabet with no 0/1/O/I. See D-003. */
export const BADGE_PATTERN = /^ZB[A-Z2-9]{10}$/;

/* ------------------------------------------------------------------ types */

export interface Branch {
  branch_id: number;
  branch_name: string;
}

export interface DeviceRegistration {
  kioskToken: string;
  branch_id: number;
  branch_name: string;
  expires_at: string;
}

export interface SessionRef {
  batch_id: number;
  label: string;
  start_time: string;
  /** Null for an open-ended batch — render the start time alone. */
  end_time: string | null;
}

export interface CheckInData {
  student_name: string;
  batch: SessionRef;
  /** Null when a coach marked the student from the portal — no scan happened. */
  scanned_in_at: string | null;
  makeup: boolean;
  /**
   * The student's next session later today at this branch, if any — shown as
   * a "See you at …" nudge (D-015). Informational only; never auto-marked.
   */
  next_session?: SessionRef | null;
}

export interface AmbiguousData {
  student_name: string;
  choices: SessionRef[];
}

/**
 * A competition drop-in visit — the separate visit log, not attendance.
 * Competitions students register for one weekly batch but attend most days;
 * on off-days their scans check them in and out of the branch instead.
 */
export interface VisitData {
  student_name: string;
  checked_in_at: string | null;
  checked_out_at: string | null;
}

/**
 * Every outcome the ScanScreen can render, as a discriminated union.
 * Note that `on_break` / `no_session` / `unknown_badge` / `unknown_student`
 * arrive over different HTTP statuses but are all *business* outcomes, so they
 * are results, not exceptions. Only `invalid_device` and transport failures are
 * exceptional.
 */
export type ScanResult =
  | { kind: "checked_in"; data: CheckInData }
  | { kind: "already_checked_in"; data: CheckInData }
  | { kind: "ambiguous"; data: AmbiguousData }
  | {
      kind: "rejected";
      status: "on_break" | "no_session" | "unknown_badge" | "unknown_student";
      message: string;
    }
  /** Scanned before the window opens, but a session exists later today. */
  | { kind: "too_early"; message: string; next: SessionRef | null }
  /** Competition drop-in: first scan of the day. */
  | { kind: "visit_in"; data: VisitData }
  /** Competition drop-in: a later scan recorded (or moved) the checkout. */
  | { kind: "visit_out"; data: VisitData }
  | { kind: "unavailable"; message: string }
  /** Client-local: a valid-looking code arrived slower than a scan. Never sent. */
  | { kind: "slow_scan"; message: string };

/** The class a matched student has today — what the confirm card asks about. */
export interface StudentSessionToday {
  batch_id: number;
  label: string;
  start_time: string;
  /** Null for an open-ended batch. */
  end_time: string | null;
  makeup: boolean;
}

/**
 * One row of the typed check-in pick list (D-018). Full first and last name —
 * the owner's call after UAT: the child has to recognise themself, and the
 * confirm card has to show which class they are about to be marked in.
 */
export interface StudentMatch {
  student_user_id: number;
  firstname: string;
  lastname: string;
  /** Their class today at this branch, or null when there is none right now. */
  session_today: StudentSessionToday | null;
  /** A Competitions-program student — checked in as a drop-in visit (D-016). */
  competition: boolean;
}

export interface TodaySession extends SessionRef {
  enrolled_count: number;
  present_count: number;
}

export interface VisitCounts {
  total_today: number;
  in_now: number;
}

export interface TodayBoard {
  branch_name: string;
  date: string;
  sessions: TodaySession[];
  /** Competition drop-ins — absent from older backends, so optional. */
  competition_visits?: VisitCounts | null;
}

export interface BadgeRecord {
  user_id: number;
  firstname: string;
  lastname: string;
  badge_code: string;
}

/** As returned by GET /api/batches/branch/:id — there is no batch name column. */
export interface BatchSummary {
  batch_id: number;
  day: string;
  start_time: string;
  end_time: string;
  active_value?: string;
  enrolled_student_count?: number;
  program_name?: string;
  delivery_type_value?: string;
  courses?: { course_code?: string; course_name?: string; show_ind?: number }[];
}

/* ------------------------------------------------------------- staff calls */

/** POST /api/auth/login — legacy shape: `{ accessToken }` at the top level. */
export async function login(email: string, password: string): Promise<string> {
  const payload = await request<{ accessToken?: string }>("/api/auth/login", {
    method: "POST",
    auth: "none",
    body: { email, password },
  });
  const token = payload?.accessToken;
  if (typeof token !== "string" || !token) {
    throw new ApiError({
      httpStatus: 200,
      status: "malformed",
      message: "Sign-in did not return a session. Try again.",
      data: payload,
    });
  }
  return token;
}

export interface StaffIdentity {
  userId: number;
  firstname: string;
  lastname: string;
}

/**
 * GET /api/auth/me — returns a bare array; the user is element 0.
 *
 * The id is what every other staff call needs; the name is what the admin bar
 * shows, so both come back from the one round trip.
 */
export async function fetchStaffIdentity(token: string): Promise<StaffIdentity> {
  const rows = await request<
    { user_id?: number; firstname?: string; lastname?: string }[]
  >("/api/auth/me", {
    auth: "staff",
    token,
  });
  const row = Array.isArray(rows) ? rows[0] : undefined;
  if (typeof row?.user_id !== "number") {
    throw new ApiError({
      httpStatus: 200,
      status: "malformed",
      message: "Could not identify your account.",
      data: rows,
    });
  }
  return {
    userId: row.user_id,
    firstname: typeof row.firstname === "string" ? row.firstname.trim() : "",
    lastname: typeof row.lastname === "string" ? row.lastname.trim() : "",
  };
}

/**
 * Branches this staff member may act for.
 *
 * GET /api/user/branches?userid=… returns a *bare array*, and returns every
 * branch when the user has no explicit branch rows (the portal's superadmin
 * fallback). We don't special-case that — a long list is still a list.
 */
export async function fetchBranches(token: string): Promise<Branch[]> {
  const { userId } = await fetchStaffIdentity(token);
  const rows = await request<Branch[]>("/api/user/branches", {
    auth: "staff",
    token,
    query: { userid: userId },
  });
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => typeof row?.branch_id === "number")
    .sort((a, b) => (a.branch_name || "").localeCompare(b.branch_name || ""));
}

/** POST /api/attendance-kiosk/device-token — the one privileged call at setup. */
export async function mintDeviceToken(
  token: string,
  branchId: number,
): Promise<DeviceRegistration> {
  const payload = await request("/api/attendance-kiosk/device-token", {
    method: "POST",
    auth: "staff",
    token,
    body: { branch_id: branchId },
  });
  return unwrap<DeviceRegistration>(payload);
}

/** GET /api/batches/branch/:id — note the envelope key is `result`, singular. */
export async function fetchBatches(
  token: string,
  branchId: number,
): Promise<BatchSummary[]> {
  const payload = await request<{ result?: BatchSummary[] }>(
    `/api/batches/branch/${branchId}`,
    { auth: "staff", token },
  );
  const rows = Array.isArray(payload?.result) ? payload.result : [];
  return rows.filter((row) => typeof row?.batch_id === "number");
}

/** POST /api/attendance-kiosk/badges — by batch, or by explicit student ids. */
export async function fetchBadges(
  token: string,
  selector: { batch_id: number } | { student_user_ids: number[] },
): Promise<BadgeRecord[]> {
  const payload = await request("/api/attendance-kiosk/badges", {
    method: "POST",
    auth: "staff",
    token,
    body: selector,
    timeoutMs: 20_000,
  });
  const rows = unwrap<BadgeRecord[]>(payload);
  return Array.isArray(rows) ? rows : [];
}

/* ------------------------------------------------------------- kiosk calls */

/**
 * POST /api/attendance-kiosk/scan — the hot path.
 *
 * Resolves for every business outcome including the red ones. Rejects only for
 * `invalid_device` (the caller must clear the token) so that path cannot be
 * accidentally rendered as a normal refusal.
 */
export async function scan(
  kioskToken: string,
  badge: string,
  batchId?: number,
): Promise<ScanResult> {
  return submitScan(kioskToken, { badge }, batchId);
}

/**
 * POST /api/attendance-kiosk/scan by student number — the typed check-in
 * (D-018). Same endpoint, same outcomes, same rules; only the identifier
 * differs. Call it only after the student has confirmed who they are.
 */
export async function checkInByStudentId(
  kioskToken: string,
  studentUserId: number,
  batchId?: number,
): Promise<ScanResult> {
  return submitScan(kioskToken, { student_user_id: studentUserId }, batchId);
}

type ScanIdentifier = { badge: string } | { student_user_id: number };

async function submitScan(
  kioskToken: string,
  identifier: ScanIdentifier,
  batchId?: number,
): Promise<ScanResult> {
  try {
    const payload = await request<Record<string, unknown>>("/api/attendance-kiosk/scan", {
      method: "POST",
      auth: "kiosk",
      token: kioskToken,
      body: batchId === undefined ? identifier : { ...identifier, batch_id: batchId },
      // Short: a student is standing there. Better a fast honest failure than
      // a long wait that ends the same way.
      timeoutMs: 8_000,
    });
    return interpretScan(payload?.status, payload);
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.isInvalidDevice) throw error;
      const outcome = interpretScanError(error);
      if (outcome) return outcome;
    }
    return {
      kind: "unavailable",
      message: "Connection problem — try again or see the coach.",
    };
  }
}

function interpretScan(status: unknown, payload: Record<string, unknown>): ScanResult {
  const data = payload?.data;
  switch (status) {
    case "checked_in":
    case "already_checked_in":
      return { kind: status, data: data as CheckInData };
    case "ambiguous": {
      const ambiguous = data as AmbiguousData | undefined;
      // A choice list we cannot render is not a choice. Degrade to a refusal
      // rather than showing an empty question the student cannot answer.
      if (ambiguous?.choices?.length) return { kind: "ambiguous", data: ambiguous };
      return {
        kind: "unavailable",
        message: "Connection problem — try again or see the coach.",
      };
    }
    case "too_early": {
      const early = data as { next_session?: SessionRef | null } | undefined;
      return {
        kind: "too_early",
        message:
          typeof payload.message === "string" && payload.message.trim()
            ? payload.message
            : "A bit early — check in closer to class time.",
        next: early?.next_session ?? null,
      };
    }
    case "visit_checked_in":
    case "visit_checked_out": {
      const visit = data as Partial<VisitData> | undefined;
      return {
        kind: status === "visit_checked_in" ? "visit_in" : "visit_out",
        data: {
          student_name: visit?.student_name ?? "",
          checked_in_at: visit?.checked_in_at ?? null,
          checked_out_at: visit?.checked_out_at ?? null,
        },
      };
    }
    case "on_break":
    case "no_session":
    case "unknown_badge":
    case "unknown_student":
      return {
        kind: "rejected",
        status,
        message:
          typeof payload.message === "string" && payload.message.trim()
            ? payload.message
            : defaultRejectionMessage(status),
      };
    default:
      return {
        kind: "unavailable",
        message: "Connection problem — try again or see the coach.",
      };
  }
}

/** 404s carry real business statuses; 503 `busy` is just unavailability. */
function interpretScanError(error: ApiError): ScanResult | null {
  if (
    error.status === "on_break" ||
    error.status === "no_session" ||
    error.status === "unknown_badge" ||
    error.status === "unknown_student"
  ) {
    return {
      kind: "rejected",
      status: error.status,
      message: error.message || defaultRejectionMessage(error.status),
    };
  }
  return null;
}

function defaultRejectionMessage(status: string): string {
  switch (status) {
    case "on_break":
      return "This enrolment is paused. Please see a coach.";
    case "no_session":
      // Fallback only — the server's own message is shown when it sends one.
      // Neutral wording: the identifier may be a badge or a typed number.
      return "No class right now — see the coach.";
    case "unknown_student":
      return "Student not found — see the front desk.";
    default:
      return "Badge not recognised. Please see a coach.";
  }
}

/** The pick list never grows past this, whatever the server sends. */
const MAX_STUDENT_MATCHES = 8;

/**
 * POST /api/attendance-kiosk/students `{ q }` — the FindScreen's lookup (D-018).
 *
 * A POST, not a GET, although it only reads: `q` is a child's name, and a
 * request body stays out of the URL — and so out of every proxy and load
 * balancer access log between the lobby and the API.
 *
 * All digits is a student-number lookup (0 or 1 rows); anything else is a name
 * prefix search the server answers with `[]` below 3 characters. Branch-scoped
 * by the device token server-side, so the client never filters by branch. It
 * does dedupe and cap: a list a child has to pick themself from must not show
 * them twice. Each row is validated field by field — `session_today` is a whole
 * session or null, `competition` is a boolean. Throws ApiError for every
 * failure — the caller keeps the input and says so inline.
 */
export async function findStudents(
  kioskToken: string,
  query: string,
  signal?: AbortSignal,
): Promise<StudentMatch[]> {
  const payload = await request("/api/attendance-kiosk/students", {
    method: "POST",
    auth: "kiosk",
    token: kioskToken,
    body: { q: query },
    // As short as the scan: the student is standing there, typing.
    timeoutMs: 8_000,
    signal,
  });
  // Untrusted until each field is checked: a row the screen cannot render must
  // not reach it.
  const rows = unwrap<Partial<Record<keyof StudentMatch, unknown>>[]>(payload);
  if (!Array.isArray(rows)) return [];
  const seen = new Set<number>();
  const matches: StudentMatch[] = [];
  for (const row of rows) {
    const id = row?.student_user_id;
    // It is written straight back to /scan, so only a real id is a match.
    if (typeof id !== "number" || !Number.isInteger(id) || id <= 0 || seen.has(id)) {
      continue;
    }
    const firstname = typeof row.firstname === "string" ? row.firstname.trim() : "";
    const lastname = typeof row.lastname === "string" ? row.lastname.trim() : "";
    // A nameless row would render as "Student": nobody can pick themself from
    // that, so it is not a match.
    if (!firstname && !lastname) continue;
    seen.add(id);
    matches.push({
      student_user_id: id,
      firstname,
      lastname,
      session_today: sessionTodayOf(row.session_today),
      // Only a real `true` — a stray "false" string must not become a label.
      competition: row.competition === true,
    });
    if (matches.length === MAX_STUDENT_MATCHES) break;
  }
  return matches;
}

/**
 * A row's `session_today`, or null. Anything that is not a whole session — a
 * numeric batch id, a string label and start time, and a string or null end
 * time (null: an open-ended batch) — is treated as "no class", and so is one
 * with nothing to show: the confirm card would rather say nothing than draw an
 * empty box.
 */
function sessionTodayOf(value: unknown): StudentSessionToday | null {
  if (!value || typeof value !== "object") return null;
  const session = value as Record<string, unknown>;
  if (
    typeof session.batch_id !== "number" ||
    !Number.isFinite(session.batch_id) ||
    typeof session.label !== "string" ||
    typeof session.start_time !== "string" ||
    (session.end_time !== null && typeof session.end_time !== "string")
  ) {
    return null;
  }
  const label = session.label.trim();
  const start = session.start_time.trim();
  const end = typeof session.end_time === "string" ? session.end_time.trim() : "";
  if (!label && !start && !end) return null;
  return {
    batch_id: session.batch_id,
    label,
    start_time: start,
    end_time: end || null,
    makeup: session.makeup === true,
  };
}

/** GET /api/attendance-kiosk/today — polled once a minute by TodayScreen. */
export async function today(kioskToken: string): Promise<TodayBoard> {
  const payload = await request("/api/attendance-kiosk/today", {
    auth: "kiosk",
    token: kioskToken,
  });
  const board = unwrap<TodayBoard>(payload);
  return {
    branch_name: board?.branch_name ?? "",
    date: board?.date ?? "",
    sessions: Array.isArray(board?.sessions) ? board.sessions : [],
    competition_visits: board?.competition_visits ?? null,
  };
}

/* ----------------------------------------------------------------- helpers */

/**
 * Compose a human label for a batch. The portal has no batch name column, so
 * this mirrors what its class table renders: day(s), time range, course codes.
 */
export function formatBatchLabel(batch: BatchSummary): string {
  const days = (batch.day || "")
    .split(",")
    .map((day) => day.trim())
    .filter(Boolean)
    .map((day) => day.slice(0, 3))
    .join("/");
  const time = formatTimeRange(batch.start_time, batch.end_time);
  const courses = (batch.courses ?? [])
    .filter((course) => course.show_ind !== 0)
    .map((course) => course.course_code)
    .filter(Boolean)
    .join(", ");
  return [days, time, courses || batch.program_name].filter(Boolean).join("  ·  ");
}

/** "16:30:00" → "4:30 PM". Tolerates ISO timestamps and junk. */
export function formatTime(value: string | null | undefined): string {
  if (!value) return "";
  const match = /^(\d{1,2}):(\d{2})/.exec(value.includes("T") ? value.slice(11) : value);
  if (!match) return value;
  const hours = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours)) return value;
  const suffix = hours < 12 ? "AM" : "PM";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${minutes} ${suffix}`;
}

/**
 * "4:00 PM–5:30 PM", or "4:00 PM" alone when the end is null (an open-ended
 * batch) or unreadable. "" when there is no start either.
 */
export function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
  separator = "–",
): string {
  return [formatTime(start), formatTime(end)].filter(Boolean).join(separator);
}

/** Local wall-clock time of an ISO instant, for "already checked in at …". */
export function formatInstant(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatTime(value);
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function firstNameOf(fullName: string | null | undefined): string {
  const trimmed = (fullName ?? "").trim();
  if (!trimmed) return "Student";
  return trimmed.split(/\s+/)[0];
}

/** "Ada", "Lovelace" → "Ada Lovelace"; whichever half is on file, or "Student". */
export function fullNameOf(
  firstname: string | null | undefined,
  lastname: string | null | undefined,
): string {
  const name = [firstname, lastname]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return name || "Student";
}

/** Whole days until an ISO expiry; negative once past. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const expiry = new Date(iso).getTime();
  if (Number.isNaN(expiry)) return null;
  return Math.floor((expiry - Date.now()) / 86_400_000);
}
